import React, { useState } from 'react';
import { 
  X, 
  UserPlus, 
  Mail, 
  Phone, 
  User, 
  Building, 
  Shield, 
  KeyRound, 
  Calendar, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Copy, 
  Check, 
  AtSign, 
  Briefcase, 
  FileText 
} from 'lucide-react';
import apiClient from '../../Api/ApiClient';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableGroups?: any[];
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  availableGroups = []
}) => {
  const [accountType, setAccountType] = useState<'INDIVIDUAL' | 'BUSINESS'>('INDIVIDUAL');
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    phoneNumber: '',
    password: '',
    groupId: '',
    expireAt: '',
    // Particulier
    firstName: '',
    lastName: '',
    // Entreprise
    businessName: '',
    legalForm: 'SARL',
    registrationNumber: '',
    repFirstName: '',
    repLastName: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUser, setCreatedUser] = useState<any | null>(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Filtrer les groupes de catégorie CUSTOMER
  const customerGroups = availableGroups.filter((g: any) => {
    const cat = (g.groupCategory || g.GroupCategory || '').toUpperCase();
    return cat === 'CUSTOMER' || cat === 'CLIENT';
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.startsWith('237') && val.length > 9) {
      val = val.slice(3);
    }
    val = val.slice(0, 9);
    setFormData(prev => ({ ...prev, phoneNumber: val }));
    setError(null);
  };

  const handleGeneratePassword = () => {
    const randomCode = Math.floor(10000 + Math.random() * 90000);
    setFormData(prev => ({ ...prev, password: `Docarn@${randomCode}` }));
  };

  const handleCopyCredentials = () => {
    if (!createdUser) return;
    const text = `Identifiants Docarn Juridique :\nNom d'utilisateur : ${createdUser.userName}\nEmail : ${createdUser.email}\nMot de passe : ${createdUser.plainPassword}\nLien de connexion : ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validations
    if (!formData.userName.trim()) {
      setError("Le nom d'utilisateur est requis.");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError("Veuillez saisir une adresse email valide.");
      return;
    }
    if (formData.phoneNumber.length !== 9) {
      setError("Le numéro de téléphone doit comporter exactement 9 chiffres (ex: 698843185).");
      return;
    }

    if (accountType === 'INDIVIDUAL') {
      if (!formData.firstName.trim() && !formData.lastName.trim()) {
        setError("Veuillez renseigner le nom ou le prénom du particulier.");
        return;
      }
    } else {
      if (!formData.businessName.trim()) {
        setError("Veuillez renseigner la raison sociale de l'entreprise.");
        return;
      }
    }

    setLoading(true);

    try {
      const payload: any = {
        accountType,
        userName: formData.userName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        password: formData.password.trim() || null,
        groupId: formData.groupId || null,
        expireAt: formData.expireAt ? new Date(formData.expireAt).toISOString() : null,
        ...(accountType === 'INDIVIDUAL' ? {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        } : {
          businessName: formData.businessName.trim(),
          legalForm: formData.legalForm.trim(),
          registrationNumber: formData.registrationNumber.trim() || null,
          repFirstName: formData.repFirstName.trim() || null,
          repLastName: formData.repLastName.trim() || null,
        })
      };

      const res = await apiClient.post<any>('/api/admin/users', payload);
      if (res && (res.success || res.Success !== false)) {
        setCreatedUser(res);
        onSuccess();
      } else {
        setError(res?.message || "Erreur lors de la création du compte.");
      }
    } catch (err: any) {
      console.error("Error creating active user:", err);
      setError(err.message || err.data?.message || "Erreur de connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    setCreatedUser(null);
    setFormData({
      userName: '',
      email: '',
      phoneNumber: '',
      password: '',
      groupId: '',
      expireAt: '',
      firstName: '',
      lastName: '',
      businessName: '',
      legalForm: 'SARL',
      registrationNumber: '',
      repFirstName: '',
      repLastName: ''
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button 
          onClick={createdUser ? handleFinish : onClose} 
          className="absolute top-5 right-5 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-primary-900 via-primary-800 to-indigo-900 p-6 text-white text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-bold uppercase tracking-wider mb-2">
            <Sparkles className="w-3 h-3 text-amber-400" /> Activation Directe
          </div>
          <h2 className="text-xl font-display font-extrabold">Créer un Utilisateur Activé</h2>
          <p className="text-primary-100 mt-1 text-xs">
            Créez un compte client immédiatement opérationnel sans envoi de lien d'activation par email.
          </p>
        </div>

        {/* Body content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {createdUser ? (
            /* SUCCESS VIEW */
            <div className="space-y-6 text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div>
                <h3 className="text-xl font-display font-bold text-zinc-900">Compte Activé avec Succès !</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Le compte est immédiatement prêt à l'emploi. Vous pouvez transmettre ces identifiants au client.
                </p>
              </div>

              {/* Credentials Card */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-5 text-left space-y-3 relative">
                <div className="flex justify-between items-center pb-2 border-b border-zinc-200">
                  <span className="text-[10px] font-bold uppercase text-zinc-400">Identifiants de Connexion</span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">Actif</span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-zinc-500 block text-[11px]">Type de Compte</span>
                    <strong className="text-zinc-800 font-bold">{createdUser.accountType === 'BUSINESS' ? '🏢 Entreprise' : '👤 Particulier'}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-500 block text-[11px]">Nom d'utilisateur</span>
                    <strong className="text-zinc-800 font-mono font-bold">{createdUser.userName}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-500 block text-[11px]">Adresse Email</span>
                    <strong className="text-zinc-800 font-bold break-all">{createdUser.email}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-500 block text-[11px]">Mot de passe défini</span>
                    <strong className="text-primary-700 font-mono font-bold bg-primary-50 px-2 py-0.5 rounded border border-primary-200">
                      {createdUser.plainPassword}
                    </strong>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCopyCredentials}
                  className="w-full mt-2 py-2 px-3 bg-white border border-zinc-300 hover:border-primary-500 text-zinc-700 hover:text-primary-700 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Identifiants copiés dans le presse-papier !" : "Copier les identifiants pour le client"}
                </button>
              </div>

              <button
                type="button"
                onClick={handleFinish}
                className="w-full py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-2xl text-xs font-bold transition-all shadow-md"
              >
                Fermer et retourner à la liste
              </button>
            </div>
          ) : (
            /* CREATION FORM */
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Account type toggle */}
              <div>
                <label className="text-xs font-bold text-zinc-700 uppercase tracking-wider block mb-2">
                  Profil du Client *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAccountType('INDIVIDUAL')}
                    className={`p-3 rounded-2xl border text-left flex items-center gap-3 transition-all ${
                      accountType === 'INDIVIDUAL'
                        ? 'border-primary-600 bg-primary-50/60 text-primary-950 ring-2 ring-primary-500/20'
                        : 'border-zinc-200 hover:border-zinc-300 text-zinc-600 bg-white'
                    }`}
                  >
                    <div className={`p-2 rounded-xl ${accountType === 'INDIVIDUAL' ? 'bg-primary-600 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs font-bold">Particulier</div>
                      <div className="text-[10px] text-zinc-500">Avocat, juriste, étudiant</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAccountType('BUSINESS')}
                    className={`p-3 rounded-2xl border text-left flex items-center gap-3 transition-all ${
                      accountType === 'BUSINESS'
                        ? 'border-primary-600 bg-primary-50/60 text-primary-950 ring-2 ring-primary-500/20'
                        : 'border-zinc-200 hover:border-zinc-300 text-zinc-600 bg-white'
                    }`}
                  >
                    <div className={`p-2 rounded-xl ${accountType === 'BUSINESS' ? 'bg-primary-600 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                      <Building className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs font-bold">Entreprise / Cabinet</div>
                      <div className="text-[10px] text-zinc-500">Société, cabinet d'avocats</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Dynamic fields: Individual vs Business */}
              {accountType === 'INDIVIDUAL' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Prénom *</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Ex: Paul"
                        className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Nom de famille *</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Ex: Biya"
                        className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-bold text-zinc-600 block mb-1">Raison Sociale / Nom Entreprise *</label>
                    <div className="relative">
                      <Building className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                      <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder="Ex: Cabinet Me Dupont & Associés"
                        className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-zinc-600 block mb-1">Forme Juridique</label>
                      <select
                        name="legalForm"
                        value={formData.legalForm}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none bg-white"
                      >
                        <option value="SARL">SARL</option>
                        <option value="SA">SA</option>
                        <option value="SAS">SAS</option>
                        <option value="ETS">ETS</option>
                        <option value="GIE">GIE</option>
                        <option value="Cabinet">Cabinet Professionnel</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[11px] font-bold text-zinc-600 block mb-1">N° RCCM / NIU (Optionnel)</label>
                      <input
                        type="text"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                        placeholder="Ex: RC/DLA/2026/B/1234"
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-zinc-600 block mb-1">Prénom du Représentant</label>
                      <input
                        type="text"
                        name="repFirstName"
                        value={formData.repFirstName}
                        onChange={handleChange}
                        placeholder="Ex: Jean"
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-zinc-600 block mb-1">Nom du Représentant</label>
                      <input
                        type="text"
                        name="repLastName"
                        value={formData.repLastName}
                        onChange={handleChange}
                        placeholder="Ex: Nguemo"
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Identifiers: UserName, Email, Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Nom d'utilisateur (Identifiant) *</label>
                  <div className="relative">
                    <AtSign className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                    <input
                      type="text"
                      name="userName"
                      value={formData.userName}
                      onChange={handleChange}
                      placeholder="Ex: cabinet.dupont"
                      className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Adresse Email *</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Ex: contact@cabinet.cm"
                      className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Phone with Cameroon badge */}
              <div>
                <label className="text-[11px] font-bold text-zinc-600 block mb-1">Numéro de Téléphone (Cameroun) *</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-1.5 px-3 py-2 border border-zinc-200 bg-zinc-50 rounded-xl text-xs font-bold text-zinc-700 select-none">
                    <span>🇨🇲</span>
                    <span>+237</span>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handlePhoneChange}
                      placeholder="698843185 (9 chiffres)"
                      maxLength={9}
                      className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none font-mono font-semibold"
                    />
                  </div>
                </div>
              </div>

              {/* Password configuration */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-[11px] font-bold text-zinc-600">Mot de passe de Connexion</label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-[11px] font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" />
                    Générer un mot de passe
                  </button>
                </div>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Laisser vide pour générer automatiquement (ex: Docarn@48291)"
                    className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none font-mono"
                  />
                </div>
              </div>

              {/* Group & Expiration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-100">
                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Groupe & Permissions</label>
                  <div className="relative">
                    <Shield className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                    <select
                      name="groupId"
                      value={formData.groupId}
                      onChange={handleChange}
                      className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none bg-white"
                    >
                      <option value="">-- Groupe par défaut ({accountType === 'BUSINESS' ? 'Entreprise' : 'Particulier'}) --</option>
                      {customerGroups.map((g: any) => (
                        <option key={g.id || g.Id} value={g.id || g.Id}>
                          {g.label || g.Label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-600 block mb-1">Date d'expiration de l'accès</label>
                  <div className="relative">
                    <Calendar className="w-4 h-4 text-zinc-400 absolute left-3 top-3" />
                    <input
                      type="date"
                      name="expireAt"
                      value={formData.expireAt}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Direct activation notice */}
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-2.5 text-emerald-800 text-xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold block">Compte activé immédiatement</span>
                  <span className="text-[11px] text-emerald-700">
                    Aucun email d'activation ne sera envoyé au client. Le compte sera instantanément fonctionnel et vérifié dans la base de données.
                  </span>
                </div>
              </div>

              {/* Error display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-2 text-red-700 text-xs font-semibold">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-xs font-bold transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-primary-600/20 transition-all disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  {loading ? "Création en cours..." : "Créer & Activer l'Utilisateur"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
